import {
  type Answers,
  type AnswerValue,
  type Question,
  type QuizDefinition,
} from "@/schema/quizSchema";

// Upper bound on how many (question, answers-so-far) states the CLASSIFIER walk (below) enqueues.
// This cap IS REACHED by real quizzes today — both `manly-quiz` and BAIA `microbiota` truncate the
// classifier walk at this budget, well before exhausting their full multi-select/slider answer
// combinatorics (manly ~157,000 non-leaf states, BAIA over 2,000,000 once every single-choice
// question's option count is multiplied out). That is expected and fine, not a bug to "fix" by
// raising the cap further. The walk is DEPTH-first (see `walkClassifier`), so hitting this cap
// still yields a SAMPLE of complete root-to-leaf paths — `classifier()` still gets called and
// checked at every leaf the walk reaches, it just doesn't reach every leaf. (An earlier,
// breadth-first version of this walk was unsound at this cap: a truncated BFS frontier never gets
// deep enough to reach a single leaf on quizzes this size, so `classifier()` was never invoked at
// all and the undeclared-persona check was silently dead — see the regression test for a
// hardcoded-undeclared-persona classifier that BFS let straight through.) It must never affect
// reachability, which is computed exhaustively by a separate, much cheaper walk (see
// `walkReachability`) that dedupes on the small set of answer keys the quiz's branching graph
// actually cares about instead of the full combinatorial answers object, so it stays exact
// regardless of this cap. Kept in the low thousands (not the ~200,000 an earlier version of this
// walk used) because this walk clones a fresh state object per branch — unlike a depth-first walk
// that can mutate-and-backtrack — so its cost scales with raw state count; 200,000 states measured
// multiple seconds of wall time per quiz at BOOT, which is the real bug CRITICAL 1 fixes
// structurally (the walk is dev/CI-only in production now) but this cap also keeps validateQuiz()
// itself fast for CI and tests.
const MAX_STATES = 5_000;

// Safety valve for the reachability walk. Unlike the classifier walk, this one does no per-state
// classifier() call — it's pure bookkeeping (object spread + Map/Set lookups) — so it can afford to
// stay exhaustive at a much higher budget than MAX_STATES for any real quiz. This cap exists only
// to bound pathological, hand-crafted inputs; hitting it degrades reachability to a diagnostic
// warning instead of a hard "unreachable question" error (see validateQuiz below).
const REACHABILITY_STATE_CAP = 2_000_000;

/** Candidate answer values the walker will branch on for one question. */
function candidates(q: Question): AnswerValue[] {
  switch (q.type) {
    case "single":
      return q.options.map((o) => o.value);
    case "multi": {
      const singles = q.options.map((o) => [o.value]);
      // The synthetic "all options at once" combo must respect `exclusive`: an exclusive option
      // (e.g. "None of these") can never coexist with other selections, so it must never appear
      // in the all-combo — it already appears on its own via `singles` above. It must also respect
      // `max`: a real user can never submit more than `max` selections, so an uncapped "every
      // non-exclusive option at once" combo can exceed `max` and hand branching/classifier a state
      // no real submission could ever produce — clamp it to the first `max` non-exclusive options.
      let nonExclusive = q.options.filter((o) => !o.exclusive).map((o) => o.value);
      if (typeof q.max === "number") nonExclusive = nonExclusive.slice(0, q.max);
      const all: AnswerValue[] = nonExclusive.length > 0 ? [nonExclusive] : [];
      const base: AnswerValue[] = (q.min ?? 0) === 0 ? [[]] : [];
      return [...base, ...singles, ...all];
    }
    case "slider": {
      const mid = (q.min + q.max) / 2;
      return [q.min, mid, q.max];
    }
    case "text":
      return ["__SAMPLE__"];
    case "number": {
      if (typeof q.min === "number" && typeof q.max === "number") {
        const mid = (q.min + q.max) / 2;
        return q.optional ? [q.min, mid, q.max, null] : [q.min, mid, q.max];
      }
      const v = q.defaultValue ?? 0;
      return q.optional ? [v, null] : [v];
    }
    case "info":
      return [null];
  }
}

/**
 * Wrap an answers object so every key actually READ off it gets recorded into `consumed`.
 * `branching` is a pure function of (answers, questionId), so "which keys did it read" is exactly
 * "which keys can change its decision" — that's how {@link runReachabilityWalk} discovers, as it
 * walks, which answer keys the branching graph actually turns out to be sensitive to.
 */
function withConsumedTracking(answers: Answers, consumed: Set<string>): Answers {
  return new Proxy(answers, {
    get(target, prop, receiver) {
      if (typeof prop === "string") consumed.add(prop);
      return Reflect.get(target, prop, receiver);
    },
    has(target, prop) {
      if (typeof prop === "string") consumed.add(prop);
      return Reflect.has(target, prop);
    },
    ownKeys(target) {
      // Conservative fallback for code that spreads/enumerates the whole answers object instead
      // of reading individual keys: treat everything currently on the object as consumed.
      for (const k of Object.keys(target)) consumed.add(k);
      return Reflect.ownKeys(target);
    },
  });
}

type ReachabilityWalkResult = {
  visited: Set<string>;
  errors: string[];
  truncated: boolean;
  usedKeys: Set<string>;
};

/**
 * One pass of the reachability walk: BFS over (question, branching-relevant-answers) states,
 * structurally similar to the classifier walk below, but deduped down to `relevantKeys` instead of
 * the full combinatorial answers object, and WITHOUT ever calling `classifier()`. `classifier()`
 * (arbitrary per-quiz persona-scoring logic) is the expensive part of a state visit, not the
 * bookkeeping — dropping it, plus the relevance-based dedupe, lets this walk stay exhaustive for
 * every quiz in the registry (it settles at a state count close to the number of questions, not the
 * number of answer combinations), so reachability, unknown-id, and cycle detection are all exact —
 * never subject to the classifier walk's truncation.
 *
 * Every `branching` call is made through the consumed-key tracking proxy, so `usedKeys` (seeded
 * with `relevantKeys`, since a key we already deduped on was necessarily read to get here) comes
 * back containing every key `branching` was ever seen to read during this pass — see
 * {@link walkReachability} for why that's exactly what's needed to find the fixpoint.
 */
function runReachabilityWalk(
  quiz: QuizDefinition,
  byId: Map<string, Question>,
  relevantKeys: ReadonlySet<string>,
): ReachabilityWalkResult {
  const errors: string[] = [];
  const visited = new Set<string>();
  const usedKeys = new Set<string>(relevantKeys);
  const seenKeys = new Set<string>();

  type WalkState = { questionId: string; answers: Answers; path: ReadonlySet<string> };

  const queue: WalkState[] = [{ questionId: quiz.questions[0].id, answers: {}, path: new Set() }];
  let head = 0;
  let stateCount = 1;
  let truncated = false;

  while (head < queue.length) {
    const { questionId, answers, path } = queue[head++];
    const q = byId.get(questionId);
    if (!q) {
      errors.push(`branching produced unknown question id: ${questionId}`);
      continue;
    }
    visited.add(questionId);
    const nextPath = new Set(path);
    nextPath.add(questionId);

    for (const value of candidates(q)) {
      const nextAnswers: Answers = q.type === "info" ? answers : { ...answers, [q.id]: value };
      const proxied = withConsumedTracking(nextAnswers, usedKeys);
      const next = quiz.branching(proxied, q.id);

      if (next === null) continue; // terminal leaf; classifier correctness is checked separately
      if (!byId.has(next)) {
        errors.push(`branching produced unknown question id: ${next}`);
        continue;
      }
      if (nextPath.has(next)) {
        errors.push(`branching cycle detected at question: ${next}`);
        continue;
      }

      const dedupeKey =
        next +
        " " +
        [...relevantKeys]
          .filter((k) => k in nextAnswers)
          .sort()
          .map((k) => `${k}=${JSON.stringify(nextAnswers[k])}`)
          .join("");
      if (seenKeys.has(dedupeKey)) continue;
      seenKeys.add(dedupeKey);

      if (stateCount < REACHABILITY_STATE_CAP) {
        stateCount += 1;
        queue.push({ questionId: next, answers: nextAnswers, path: nextPath });
      } else {
        truncated = true;
      }
    }
  }

  return { visited, errors, truncated, usedKeys };
}

/**
 * Exhaustive reachability walk, iterated to a FIXPOINT over which answer keys the walk needs to
 * dedupe on.
 *
 * Why a fixpoint instead of a separate up-front sensitivity pass: an earlier version of this file
 * discovered "branching-relevant" keys with a one-key-at-a-time perturbation pass, independent of
 * the walk itself. That missed any key `branching` only reads CONDITIONALLY, at a question the
 * perturbation pass's baseline walk never happened to reach — a real quiz can have a genuinely
 * reachable question falsely reported `unreachable question: ...` as a result (see the regression
 * test for the shape). Deriving the relevant-key set from the walk's OWN `branching` reads instead
 * is sound by construction: round 1 runs `runReachabilityWalk` with an empty relevant-key set
 * (so every state dedupes only on the target question id) and records every key `branching`
 * actually read anywhere it was called; if that's a strict superset of what the walk deduped on,
 * some read could have been on a value the dedupe collapsed away, so round 2 reruns with the wider
 * set, and so on. This is monotone — the key set only grows, and it's bounded by the quiz's finite
 * set of answer keys — so it always terminates, typically in 1-3 rounds for real quizzes.
 */
function walkReachability(
  quiz: QuizDefinition,
  byId: Map<string, Question>,
): { visited: Set<string>; errors: string[]; truncated: boolean } {
  let relevantKeys: ReadonlySet<string> = new Set<string>();
  // Defensive round cap: the fixpoint is provably monotone and bounded by the number of distinct
  // answer keys in the quiz, so this should never be hit — it exists only to fail safe (rather than
  // hang) if a future change to `branching` or `candidates` broke that invariant.
  const maxRounds = quiz.questions.length + 10;

  let result = runReachabilityWalk(quiz, byId, relevantKeys);
  for (let round = 0; round < maxRounds; round += 1) {
    if (result.usedKeys.size === relevantKeys.size) break; // fixpoint: no new key was discovered
    relevantKeys = result.usedKeys;
    result = runReachabilityWalk(quiz, byId, relevantKeys);
  }

  return { visited: result.visited, errors: result.errors, truncated: result.truncated };
}

/**
 * Capped combinatorial walk, kept ONLY for the classifier/undeclared-persona check (does the
 * classifier ever return an undeclared persona key). This is the walk that can genuinely be
 * expensive — it calls the quiz's own `classifier()` at every leaf, and every multi-select/slider
 * combination is a distinct state because the classifier (unlike branching, per the registry today)
 * is allowed to look at all of them — so it stays budgeted at MAX_STATES and is explicitly sampled,
 * not exhaustive, once truncated. It intentionally does NOT drive reachability or
 * unreachable-question errors; see `walkReachability` for those, which stays exact regardless of
 * whether this walk truncates.
 *
 * DEPTH-first by construction (a LIFO stack, not a FIFO queue). `classifier()` is only ever called
 * at a LEAF — the last question on a path, where `branching` returns `null` — so what MAX_STATES
 * truncation actually samples matters: a breadth-first walk expands every state at depth N before
 * any state at depth N+1, so on a quiz whose full leaf depth needs more states than MAX_STATES to
 * reach, a truncated BFS frontier never gets deep enough to reach a SINGLE leaf and `classifier()`
 * is never invoked at all — silently disabling the undeclared-persona check for that quiz instead
 * of sampling it. A depth-first walk instead completes whole root-to-leaf paths before backtracking
 * to try the next branch, so a truncated DFS still yields a genuine (if partial) sample of complete
 * paths with `classifier()` exercised at every one of them.
 */
function walkClassifier(
  quiz: QuizDefinition,
  byId: Map<string, Question>,
  declaredKeys: Set<string>,
): { errors: string[]; truncated: boolean } {
  const errors: string[] = [];

  type WalkState = { questionId: string; answers: Answers; path: ReadonlySet<string> };

  const stack: WalkState[] = [{ questionId: quiz.questions[0].id, answers: {}, path: new Set() }];
  let stateCount = 1;
  let truncated = false;

  while (stack.length > 0) {
    const { questionId, answers, path } = stack.pop()!;
    const q = byId.get(questionId);
    if (!q) continue; // unknown-id already reported by the exhaustive reachability walk
    const nextPath = new Set(path);
    nextPath.add(questionId);

    for (const value of candidates(q)) {
      const nextAnswers: Answers = q.type === "info" ? answers : { ...answers, [q.id]: value };
      const next = quiz.branching(nextAnswers, q.id);
      if (next === null) {
        const persona = quiz.classifier(nextAnswers);
        if (!declaredKeys.has(persona)) {
          errors.push(`classifier returned undeclared persona key: ${persona}`);
        }
      } else if (!byId.has(next)) {
        // reported by the exhaustive reachability walk
      } else if (nextPath.has(next)) {
        // reported by the exhaustive reachability walk
      } else if (stateCount < MAX_STATES) {
        stateCount += 1;
        stack.push({ questionId: next, answers: nextAnswers, path: nextPath });
      } else {
        truncated = true;
      }
    }
  }

  return { errors, truncated };
}

export function validateQuiz(quiz: QuizDefinition): string[] {
  const declaredKeys = new Set(quiz.personas.map((p) => p.key));
  const errors: string[] = [];

  // 1. Unique ids.
  const ids = new Set<string>();
  for (const q of quiz.questions) {
    if (ids.has(q.id)) errors.push(`duplicate question id: ${q.id}`);
    ids.add(q.id);
  }
  if (quiz.questions.length === 0) return ["quiz has no questions"];

  const byId = new Map(quiz.questions.map((q) => [q.id, q]));

  // 2. Reachability + unknown-id + cycle detection: exhaustive (see walkReachability doc comment).
  const { visited, errors: reachErrors, truncated: reachTruncated } = walkReachability(quiz, byId);
  errors.push(...reachErrors);

  // 3. Classifier/undeclared-persona check: capped combinatorial walk, sampled if truncated.
  const { errors: classifierErrors, truncated: classifierTruncated } = walkClassifier(
    quiz,
    byId,
    declaredKeys,
  );
  errors.push(...classifierErrors);

  if (reachTruncated) {
    // An inexhaustive reachability walk cannot tell "genuinely unreachable" from "never got to
    // it" — so it must never drive a hard error. Surface the truncation as a diagnostic instead of
    // failing a quiz that might well be perfectly valid.
    console.warn(
      `validateQuiz: reachability walk for "${quiz.slug}" hit the ${REACHABILITY_STATE_CAP}-state ` +
        `safety cap before exhausting the quiz's branching state space. Skipping the ` +
        `unreachable-question check for this quiz — treat this as a sign the quiz's branching state ` +
        `space is unusually large and worth reviewing, not a validateQuiz bug.`,
    );
  } else {
    for (const q of quiz.questions) {
      if (!visited.has(q.id)) errors.push(`unreachable question: ${q.id}`);
    }
  }

  if (classifierTruncated) {
    console.warn(
      `validateQuiz: classifier/undeclared-persona check for "${quiz.slug}" hit the ${MAX_STATES}-state ` +
        `budget; that check is sampled, not exhaustive, for this quiz. Reachability is unaffected (see ` +
        `walkReachability) and is exact regardless of this truncation.`,
    );
  }

  // De-duplicate (the same structural error can surface on many paths).
  return [...new Set(errors)];
}
